import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getScrambleWords, scrambleWord, detectSubjectFromTopic } from "@/constants/wordScramble";

const WAITING_MESSAGES = [
  "Almost there! Priya is rehearsing her lines...",
  "Bunty is practicing his punchlines...",
  "Your story is being illustrated...",
  "Just a few more seconds...",
];

export const WordScramble = ({ subject, topicText }) => {
  const [words, setWords] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [scrambledLetters, setScrambledLetters] = useState([]);
  const [answerSlots, setAnswerSlots] = useState([]);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'wrong' | null
  const [score, setScore] = useState(0);
  const [allDone, setAllDone] = useState(false);
  const [waitMsgIdx, setWaitMsgIdx] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const feedbackTimeout = useRef(null);

  // Initialize words on mount
  useEffect(() => {
    const detectedSubject = subject || detectSubjectFromTopic(topicText);
    const picked = getScrambleWords(detectedSubject, topicText, 8);
    setWords(picked);
  }, [subject, topicText]);

  // Setup current word
  useEffect(() => {
    if (words.length === 0 || currentIdx >= words.length) return;
    const entry = words[currentIdx];
    const wordStr = entry.word || entry;
    const scrambled = scrambleWord(wordStr);
    setScrambledLetters(
      scrambled.split("").map((letter, i) => ({ id: `${i}-${letter}`, letter, placed: false }))
    );
    setAnswerSlots(Array(wordStr.length).fill(null));
    setFeedback(null);
    setShowHint(false);
  }, [words, currentIdx]);

  // Rotating wait messages when all done
  useEffect(() => {
    if (!allDone) return;
    const t = setInterval(() => setWaitMsgIdx(i => (i + 1) % WAITING_MESSAGES.length), 3000);
    return () => clearInterval(t);
  }, [allDone]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => { if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current); };
  }, []);

  const currentEntry = words[currentIdx];
  const currentWord = (currentEntry?.word || currentEntry || "");
  const currentConcept = currentEntry?.concept || "";

  // Tap a scrambled letter -> place in next empty slot
  const handleLetterTap = useCallback((letterId) => {
    if (feedback) return; // locked during feedback
    const letterObj = scrambledLetters.find(l => l.id === letterId);
    if (!letterObj || letterObj.placed) return;

    const emptyIdx = answerSlots.indexOf(null);
    if (emptyIdx === -1) return;

    const newScrambled = scrambledLetters.map(l =>
      l.id === letterId ? { ...l, placed: true } : l
    );
    const newSlots = [...answerSlots];
    newSlots[emptyIdx] = letterObj;

    setScrambledLetters(newScrambled);
    setAnswerSlots(newSlots);

    // Check if word is complete
    if (newSlots.every(s => s !== null)) {
      const attempt = newSlots.map(s => s.letter).join("");
      if (attempt === currentWord.toUpperCase()) {
        setFeedback("correct");
        setScore(prev => prev + 1);
        feedbackTimeout.current = setTimeout(() => {
          if (currentIdx + 1 >= words.length) {
            setAllDone(true);
          } else {
            setCurrentIdx(prev => prev + 1);
          }
        }, 1000);
      } else {
        setFeedback("wrong");
        feedbackTimeout.current = setTimeout(() => {
          // Reset — send all letters back
          setScrambledLetters(prev => prev.map(l => ({ ...l, placed: false })));
          setAnswerSlots(Array(currentWord.length).fill(null));
          setFeedback(null);
        }, 800);
      }
    }
  }, [scrambledLetters, answerSlots, feedback, currentWord, currentIdx, words.length]);

  // Tap an answer slot -> send letter back
  const handleSlotTap = useCallback((slotIdx) => {
    if (feedback) return;
    const slot = answerSlots[slotIdx];
    if (!slot) return;

    const newSlots = [...answerSlots];
    newSlots[slotIdx] = null;

    const newScrambled = scrambledLetters.map(l =>
      l.id === slot.id ? { ...l, placed: false } : l
    );

    setAnswerSlots(newSlots);
    setScrambledLetters(newScrambled);
  }, [answerSlots, scrambledLetters, feedback]);

  if (words.length === 0) return null;

  // All words done — show waiting messages
  if (allDone) {
    return (
      <div style={{ textAlign: "center", padding: "20px 16px" }}>
        <p style={{
          fontFamily: "'Patrick Hand', cursive", fontSize: 20,
          color: "#66BB6A", marginBottom: 8,
        }}>
          {score}/{words.length} words unscrambled!
        </p>
        <motion.p
          key={waitMsgIdx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          style={{
            fontFamily: "'Inter', sans-serif", fontSize: 14,
            color: "#666", marginTop: 12,
          }}
        >
          {WAITING_MESSAGES[waitMsgIdx]}
        </motion.p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 360, margin: "0 auto", padding: "12px 0" }}>
      {/* Heading */}
      <p style={{
        fontFamily: "'Patrick Hand', cursive", fontSize: 18,
        color: "#448AFF", textAlign: "center", margin: "0 0 4px 0",
      }}>
        Unscramble while you wait!
      </p>
      <p style={{
        fontFamily: "'Inter', sans-serif", fontSize: 12,
        color: "#666", textAlign: "center", margin: "0 0 16px 0",
      }}>
        Word {currentIdx + 1} of {words.length}
        {score > 0 && <span style={{ color: "#66BB6A", marginLeft: 8 }}>{score} correct</span>}
      </p>

      {/* Answer slots */}
      <div style={{
        display: "flex", justifyContent: "center", gap: 6,
        marginBottom: 20, minHeight: 48,
      }}>
        {answerSlots.map((slot, i) => (
          <motion.button
            key={i}
            onClick={() => handleSlotTap(i)}
            animate={
              feedback === "wrong"
                ? { x: [0, -4, 4, -4, 4, 0] }
                : feedback === "correct"
                ? { scale: [1, 1.1, 1] }
                : {}
            }
            transition={{ duration: feedback === "wrong" ? 0.4 : 0.3 }}
            style={{
              width: 40, height: 44,
              border: `1.5px ${slot ? "solid" : "dashed"} ${
                feedback === "correct" ? "#66BB6A" :
                feedback === "wrong" ? "#FF6B6B" : "#CCCCCC"
              }`,
              borderRadius: 4,
              background: slot ? "#448AFF" : "transparent",
              color: slot ? "#fff" : "transparent",
              fontSize: 20,
              fontFamily: "'Patrick Hand', cursive",
              cursor: slot ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {slot ? slot.letter : ""}
          </motion.button>
        ))}
      </div>

      {/* Feedback text */}
      <AnimatePresence>
        {feedback === "correct" && (
          <motion.p
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            style={{
              textAlign: "center", fontFamily: "'Patrick Hand', cursive",
              fontSize: 16, color: "#66BB6A", margin: "0 0 12px 0",
            }}
          >
            Correct! The word is {currentWord.toUpperCase()}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Scrambled letters */}
      <div style={{
        display: "flex", justifyContent: "center", gap: 6,
        flexWrap: "wrap",
      }}>
        {scrambledLetters.map((l) => (
          <motion.button
            key={l.id}
            onClick={() => handleLetterTap(l.id)}
            whileTap={{ scale: 0.9 }}
            style={{
              width: 44, height: 44,
              border: "1.5px dashed #CCCCCC",
              borderRadius: 4,
              background: l.placed ? "rgba(68,138,255,0.08)" : "#fff",
              color: l.placed ? "transparent" : "#1A1A2E",
              fontSize: 20,
              fontFamily: "'Patrick Hand', cursive",
              cursor: l.placed ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: l.placed ? 0.3 : 1,
            }}
          >
            {l.letter}
          </motion.button>
        ))}
      </div>

      {/* Hint button + hint text */}
      {currentConcept && (
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <button
            onClick={() => setShowHint(h => !h)}
            style={{
              background: "transparent", border: "none",
              fontSize: 14, color: "#E6A800", cursor: "pointer",
              fontFamily: "'Patrick Hand', cursive",
              display: "inline-flex", alignItems: "center", gap: 4,
            }}
          >
            <span role="img" aria-label="hint">💡</span>
            {showHint ? "Hide hint" : "Need a hint?"}
          </button>
          <AnimatePresence>
            {showHint && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                style={{
                  marginTop: 8, padding: "8px 14px",
                  border: "1.5px dashed #E6A800",
                  background: "rgba(255,215,64,0.1)",
                  borderRadius: 4,
                  fontSize: 13, fontFamily: "'Patrick Hand', cursive",
                  color: "#1A1A2E",
                }}
              >
                {currentConcept}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};
